import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  Shield, CheckCircle2, FileCheck, Users, Clock, 
  Target, AlertTriangle, Eye, Lock, RefreshCw
} from "lucide-react";
import { TrustNavigation } from "@/components/TrustNavigation";
import { TrustFooter } from "@/components/TrustFooter";

export default function ProcessControls() {

  const controls = [
    {
      category: "Quality Assurance",
      icon: CheckCircle2,
      items: [
        {
          title: "Dual Review Protocol",
          description: "All candidate dossiers undergo agent review before publication. Critical assessments may require secondary review."
        },
        {
          title: "Standardized Scoring Rubrics",
          description: "Role-specific rubrics ensure consistent evaluation criteria across all assessments."
        },
        {
          title: "Video Quality Checks",
          description: "Assessment recordings are verified for technical quality before assessment processing."
        }
      ]
    },
    {
      category: "Data Integrity",
      icon: Lock,
      items: [
        {
          title: "Immutable Recording",
          description: "Video assessments are recorded without pause or retake capability to ensure authenticity."
        },
        {
          title: "Timestamp Verification",
          description: "All submissions, responses, and actions are timestamped for audit trail integrity."
        },
        {
          title: "Version Control",
          description: "Document versions are tracked with change summaries for complete revision history."
        }
      ]
    },
    {
      category: "Access Controls",
      icon: Eye,
      items: [
        {
          title: "Tiered Visibility",
          description: "Employer access is controlled by tier (Core, Professional, Enterprise) with field-level restrictions."
        },
        {
          title: "Agent-Controlled Publishing",
          description: "Candidates are only visible to employers after explicit agent publication action."
        },
        {
          title: "Secure Share Links",
          description: "Dossier access uses unique, expirable tokens with usage tracking."
        }
      ]
    },
    {
      category: "Verification Procedures",
      icon: FileCheck,
      items: [
        {
          title: "Credential Cross-Reference",
          description: "Claimed qualifications are verified against available certification databases and documentation."
        },
        {
          title: "Consistency Scoring",
          description: "Written and verbal responses are compared for alignment and flagged inconsistencies."
        },
        {
          title: "Experience Validation",
          description: "Work history claims are assessed through structured assessment questions designed to verify depth of experience."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#05070a", color: "#ffffff" }}>
      <TrustNavigation currentPath="/process-controls" />

      <section className="relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950" />
        <div className="container mx-auto px-4 py-10 md:py-24 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium border border-white/20 mb-6">
              <Shield className="w-4 h-4" />
              <span>Process Controls</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Quality & Security Controls
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Documented procedures and controls that ensure consistent, secure, and auditable hiring operations.
            </p>
            <p className="text-sm text-slate-400 mt-4">
              Last updated: January 2026 | Version 1.4
            </p>
          </div>
        </div>
      </section>

      <section className="py-16" style={{ backgroundColor: "#05070a" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="space-y-12">
              {controls.map((category) => (
                <div key={category.category}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(99,102,241,0.2)" }}>
                      <category.icon className="w-5 h-5 text-indigo-400" />
                    </div>
                    <h2 className="text-xl font-bold text-white">{category.category}</h2>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    {category.items.map((item, idx) => (
                      <Card key={idx} className="p-5 bg-slate-800/30 border-white/10 hover:border-indigo-500/30 transition-all">
                        <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                        <p className="text-sm text-slate-400">{item.description}</p>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16" style={{ backgroundColor: "#080b12" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Review Cycle</h2>
            <Card className="p-6 bg-slate-800/30 border-white/10">
              <div className="flex items-start gap-4">
                <RefreshCw className="w-8 h-8 text-slate-400 flex-shrink-0" />
                <div>
                  <p className="text-slate-300 mb-4">
                    Process controls are reviewed quarterly to ensure they remain effective and aligned with operational requirements. 
                    Changes are documented in the platform change log with version tracking.
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="px-4 py-2 rounded-lg" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
                      <span className="text-slate-500">Last Review:</span>
                      <span className="font-medium text-slate-200 ml-2">January 2026</span>
                    </div>
                    <div className="px-4 py-2 rounded-lg" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
                      <span className="text-slate-500">Next Review:</span>
                      <span className="font-medium text-slate-200 ml-2">April 2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      <TrustFooter />
    </div>
  );
}
